// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState } from 'react';
import './CategoryBarChart.css';

const CATEGORY_ICONS = {
  general: '🏛️',
  golf: '⛳',
  dining: '🍽️',
  amenities: '🎾',
  membership: '🪪',
  personnel: '👥',
};

const SENTIMENT_ORDER = ['POSITIVE', 'MIXED', 'NEUTRAL', 'NEGATIVE'];

export default function CategoryBarChart({ data, total, sentimentMeta }) {
  const [showSentiment, setShowSentiment] = useState(false);

  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="cat-chart">
      <div className="cat-chart__toggle">
        <button
          className={`cat-chart__toggle-btn${!showSentiment ? ' cat-chart__toggle-btn--active' : ''}`}
          onClick={() => setShowSentiment(false)}
          aria-pressed={!showSentiment}
        >
          Volume
        </button>
        <button
          className={`cat-chart__toggle-btn${showSentiment ? ' cat-chart__toggle-btn--active' : ''}`}
          onClick={() => setShowSentiment(true)}
          aria-pressed={showSentiment}
        >
          + Sentiment
        </button>
      </div>

      <div className="cat-chart__bars" role="list" aria-label="Feedback count by category">
        {data.filter((d) => d.count > 0).map((d) => {
          const barWidth = (d.count / maxCount) * 100;
          const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;

          return (
            <div
              key={d.category}
              className="cat-chart__row"
              role="listitem"
            >
              <div className="cat-chart__label">
                <span className="cat-chart__icon" aria-hidden="true">
                  {CATEGORY_ICONS[d.category] || '📁'}
                </span>
                <span className="cat-chart__name">
                  {d.category.charAt(0).toUpperCase() + d.category.slice(1)}
                </span>
              </div>

              <div className="cat-chart__bar-wrap" aria-label={`${d.count} submissions (${pct}%)`}>
                {showSentiment ? (
                  // Stacked sentiment bar
                  <div className="cat-chart__bar cat-chart__bar--stacked">
                    {SENTIMENT_ORDER.map((sent) => {
                      const segCount = d.sentiment[sent] || 0;
                      const segWidth = d.count > 0 ? (segCount / d.count) * barWidth : 0;
                      if (segWidth === 0) return null;
                      return (
                        <div
                          key={sent}
                          className="cat-chart__bar-seg"
                          style={{
                            width: `${segWidth}%`,
                            background: sentimentMeta[sent].color,
                          }}
                          title={`${sentimentMeta[sent].label}: ${segCount}`}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div
                    className="cat-chart__bar"
                    style={{ width: `${barWidth}%` }}
                    aria-hidden="true"
                  />
                )}
              </div>

              <div className="cat-chart__count">
                <strong>{d.count}</strong>
                <span className="cat-chart__pct">{pct}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {showSentiment && (
        <div className="cat-chart__legend" aria-label="Sentiment color legend">
          {SENTIMENT_ORDER.map((sent) => (
            <span key={sent} className="cat-chart__legend-item">
              <span
                className="cat-chart__legend-dot"
                style={{ background: sentimentMeta[sent].color }}
                aria-hidden="true"
              />
              {sentimentMeta[sent].label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
